const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type AiSpecPayload = {
  role: string;
  teamFocus?: string;
  summary?: string;
  location?: string;
  type?: string;
  salary?: string;
  requirements?: string[];
  responsibilities?: string[];
  values?: string[];
  tone?: 'inclusive' | 'bold';
  occupationId?: string;
  occupationTitle?: string;
};

export type InclusiveLanguageHint = {
  phrase: string;
  suggestion: string;
  category: 'gendered' | 'ableist' | 'corporate';
  severity: 'info' | 'warning';
};

export type AiSpecResponse = {
  summary: string;
  responsibilities: string;
  requirements: string;
  callToAction: string;
  inclusiveHints: InclusiveLanguageHint[];
};

const fallbackResponsibilities = [
  'Lead a small squad to deliver reliable, secure features for candidates and employers',
  'Translate discovery insights into clear outcomes and roadmap slices',
  'Collaborate with design, data, and operations to improve hiring flows',
  'Champion accessibility, inclusive hiring, and flexible work practices',
];

const fallbackRequirements = [
  '5+ years shipping production-grade software or digital products',
  'Experience working with cloud-first architectures and modern tooling',
  'Clear, concise communication across engineering, product, and stakeholders',
  'Comfort with iterative delivery, observability, and hypothesis-driven work',
];

const formatBullets = (lines: string[]) => lines.map((line) => `• ${line}`).join('\n');

const buildFallbackResponse = (payload: AiSpecPayload): AiSpecResponse => {
  const summaryBits = [
    `Create a ${payload.role || 'role'}`,
    payload.location ? `based in ${payload.location}` : null,
    payload.teamFocus ? `focused on ${payload.teamFocus.toLowerCase()}` : null,
    'with flexible, inclusive ways of working.',
  ]
    .filter(Boolean)
    .join(' ');

  const responsibilities = payload.responsibilities?.length
    ? formatBullets(payload.responsibilities)
    : formatBullets(fallbackResponsibilities);

  const requirements = payload.requirements?.length
    ? formatBullets(payload.requirements)
    : formatBullets(fallbackRequirements);

  const callToAction =
    payload.tone === 'bold'
      ? 'Ready to help us ship inclusive hiring experiences? Share your impact and let’s talk.'
      : 'If this sounds like you, we would love to hear your story and discuss how we can support your success.';

  return {
    summary: summaryBits,
    responsibilities,
    requirements,
    callToAction,
    inclusiveHints: [],
  };
};

export async function generateJobSpec(payload: AiSpecPayload): Promise<AiSpecResponse> {
  if (!API_BASE_URL) {
    return buildFallbackResponse(payload);
  }

  const response = await fetch(`${API_BASE_URL}/v1/ai/spec`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Request failed');
    throw new Error(`AI spec generation failed (${response.status}): ${errorText}`);
  }

  return (await response.json()) as AiSpecResponse;
}
